import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, MXN } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", category: "Cabello", price: "", duration: "", description: "", promo: "", image: "" };

export default function Services() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/services").then((r) => setServices(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...s, promo: s.promo || "", image: s.image || "" });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      price: Number(form.price),
      duration: Number(form.duration),
      promo: form.promo || null,
    };
    try {
      if (editing) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing ? "Servicio actualizado" : "Servicio creado");
      setOpen(false);
      load();
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success("Servicio eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div data-testid="admin-services" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-display text-3xl">Servicios</h1>
          <p className="text-sm text-muted-foreground">{services.length} servicios</p>
        </div>
        <button
          data-testid="service-new-btn"
          onClick={openNew}
          className="flex items-center gap-2 bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
        >
          <Plus size={16} /> Nuevo
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {services.map((s) => (
          <div key={s.id} data-testid={`service-admin-${s.id}`} className="border border-border bg-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-primary">{s.category}</p>
                <h3 className="font-serif-display text-xl">{s.name}</h3>
              </div>
              <span className="font-serif-display text-lg text-primary">{MXN(s.price)}</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{s.duration} min</p>
            <div className="mt-4 flex gap-2">
              <button
                data-testid={`service-edit-${s.id}`}
                onClick={() => openEdit(s)}
                className="flex items-center gap-1 border border-border px-3 py-1.5 text-xs transition-colors hover:border-primary"
              >
                <Pencil size={13} /> Editar
              </button>
              <button
                data-testid={`service-delete-${s.id}`}
                onClick={() => remove(s.id)}
                className="flex items-center gap-1 border border-border px-3 py-1.5 text-xs text-destructive transition-colors hover:border-destructive"
              >
                <Trash2 size={13} /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing ? "Editar servicio" : "Nuevo servicio"}
            </DialogTitle>
            <DialogDescription>Completa la información del servicio.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Nombre</Label>
              <Input data-testid="service-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Categoría</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div>
              <Label>Precio (MXN)</Label>
              <Input data-testid="service-input-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <Label>Duración (min)</Label>
              <Input data-testid="service-input-duration" type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
            </div>
            <div>
              <Label>Promo (opcional)</Label>
              <Input value={form.promo} onChange={(e) => setForm({ ...form, promo: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Imagen (URL)</Label>
              <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="service-save-btn"
              onClick={save}
              className="bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
            >
              Guardar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
